"use client";
import s from "./SectionFAQ.module.scss";
import { AccordionItem, AccordionItemProps } from "components/shared/AccordionItem";
import { Section, SectionProps } from "@/components/layout/Section";
import cn from "classnames";
import { useCallback, useEffect, useRef } from "react";
import { debounce } from "lodash";

type SectionFAQProps = Omit<SectionProps, "icon"> & {
  items: AccordionItemProps[];
  cols?: boolean;
  ordered?: boolean;
};

export const SectionFAQ = ({ heading, items, subheading, cols = true, ordered = true }: SectionFAQProps) => {
  const splitIndex = Math.ceil(items.length / 2);

  const wrapperRef = useRef<HTMLDivElement>(null);

  const groups = cols ? [items.slice(0, splitIndex), items.slice(splitIndex)] : [items];

  const adjustQuestionsHeight = useCallback(() => {
    if (!cols || !wrapperRef.current) return;
    const items = Array.from(wrapperRef.current.querySelectorAll(".sb-accordion-item-question"));

    items.forEach((item) => {
      item.removeAttribute("style");
    });

    if (window.matchMedia("(max-width: 992px)").matches) return;

    const groups = [items.slice(0, splitIndex), items.slice(splitIndex)];

    // compare height of elements
    groups[0].forEach((el, index) => {
      const neighbourElement = groups[1][index];
      if (!neighbourElement) return;
      if (el.scrollHeight < neighbourElement.scrollHeight) {
        el.setAttribute("style", `min-height:${neighbourElement.scrollHeight}px`);
      } else {
        neighbourElement.setAttribute("style", `min-height:${el.scrollHeight}px`);
      }
    });
  }, [cols, splitIndex]);

  useEffect(() => {
    const debouncedAdjustHeight = debounce(adjustQuestionsHeight, 20);
    adjustQuestionsHeight();
    window.addEventListener("resize", debouncedAdjustHeight);
    return () => {
      window.removeEventListener("resize", debouncedAdjustHeight);
    };
  }, [adjustQuestionsHeight]);

  return (
    <Section icon="/img/icons/sections/icon-faq.svg" heading={heading} subheading={subheading}>
      <div
        className={cn(s.wrapper, { [s.withCols]: cols })}
        itemScope
        itemType="https://schema.org/FAQPage"
        ref={wrapperRef}
      >
        {groups.map((items, groupIndex) => (
          <div className={s.group} key={groupIndex}>
            {items.map((item, index) => (
              <AccordionItem
                key={index}
                {...item}
                className={s.sectionFAQItem}
                orderNumber={ordered ? (groupIndex ? splitIndex + index + 1 : index + 1) : undefined}
              />
            ))}
          </div>
        ))}
      </div>
    </Section>
  );
};
